
import { useState } from 'react';
import Header from '../../components/base/Header';
import Footer from '../../components/base/Footer';
import { birthFlowers } from '../../mocks/birthFlowers';

export default function TodayFlowerPage() {
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState<number>(new Date().getDate());
  const [showResult, setShowResult] = useState(false);

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const getDaysInMonth = (month: number) => {
    const year = new Date().getFullYear();
    return new Date(year, month, 0).getDate();
  };

  const days = Array.from({ length: getDaysInMonth(selectedMonth) }, (_, i) => i + 1);

  const getFlowerData = (month: number, day: number) => {
    const key = `${month}-${day}`;
    return birthFlowers[key as keyof typeof birthFlowers] || {
      name: '季節の花',
      language: '美しさ、希望',
      description: 'この日の誕生花は季節の美しい花々です。',
      trivia: 'この日の誕生花について、詳しい情報は準備中です。'
    };
  };

  const handleSearch = () => {
    setShowResult(true);
  };

  const handleTodayFlower = () => {
    const today = new Date();
    setSelectedMonth(today.getMonth() + 1);
    setSelectedDay(today.getDate());
    setShowResult(true);
  };

  const currentFlower = getFlowerData(selectedMonth, selectedDay);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      <Header />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* ヘッダーセクション */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full mb-6 shadow-lg">
              <i className="ri-flower-line text-4xl text-white"></i>
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-4">今日の誕生花を探す</h1>
            <p className="text-lg text-gray-600">あなたの誕生日の花を見つけましょう</p>
          </div>

          {/* 検索セクション */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <div className="flex flex-col md:flex-row gap-6 items-end">
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 mb-2">月</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => {
                    setSelectedMonth(Number(e.target.value));
                    setShowResult(false);
                  }}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-400 focus:outline-none transition-colors text-base"
                >
                  {months.map((month) => (
                    <option key={month} value={month}>
                      {month}月
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 mb-2">日</label>
                <select
                  value={selectedDay}
                  onChange={(e) => {
                    setSelectedDay(Number(e.target.value));
                    setShowResult(false);
                  }}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-400 focus:outline-none transition-colors text-base"
                >
                  {days.map((day) => (
                    <option key={day} value={day}>
                      {day}日
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleSearch}
                className="whitespace-nowrap px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl cursor-pointer"
              >
                <i className="ri-search-line mr-2"></i>
                誕生花を探す
              </button>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={handleTodayFlower}
                className="whitespace-nowrap w-full px-6 py-3 bg-gradient-to-r from-teal-400 to-cyan-500 text-white font-semibold rounded-xl hover:from-teal-500 hover:to-cyan-600 transition-all shadow-md hover:shadow-lg cursor-pointer"
              >
                <i className="ri-calendar-check-line mr-2"></i>
                今日の誕生花を見る
              </button>
            </div>
          </div>

          {/* 結果表示セクション */}
          {showResult && (
            <div className="mt-12">
              <div 
                className="relative bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 rounded-3xl shadow-2xl overflow-hidden"
              >
                {/* 装飾的な花のパターン背景 */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-10 left-10 text-6xl">🌸</div>
                  <div className="absolute top-20 right-20 text-5xl">🌺</div>
                  <div className="absolute bottom-20 left-20 text-5xl">🌼</div>
                  <div className="absolute bottom-10 right-10 text-6xl">🌷</div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-7xl">✿</div>
                </div>

                <div className="relative z-10 p-12">
                  <div className="text-center mb-8">
                    <div className="inline-block bg-white/80 backdrop-blur-sm px-8 py-3 rounded-full shadow-lg mb-4">
                      <p className="text-lg text-gray-600 font-medium">
                        {selectedMonth}月{selectedDay}日の誕生花
                      </p>
                    </div>
                    <h2 className="text-5xl font-bold text-gray-800 mb-4">
                      {currentFlower.name}
                    </h2>
                    <div className="text-7xl mb-6">
                      🌸
                    </div>
                  </div>

                  <div className="max-w-3xl mx-auto space-y-6">
                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
                      <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                        <i className="ri-heart-line text-pink-500 mr-3"></i>
                        花言葉
                      </h3>
                      <p className="text-xl text-gray-700 leading-relaxed">
                        「{currentFlower.language}」
                      </p>
                    </div>

                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
                      <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                        <i className="ri-chat-smile-3-line text-purple-500 mr-3"></i>
                        モモちゃんからのメッセージ
                      </h3>
                      <p className="text-gray-700 leading-relaxed text-lg">
                        {currentFlower.description}
                      </p>
                    </div>
                  </div>

                  <div className="text-center mt-8">
                    <button
                      onClick={() => setShowResult(false)}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap shadow-lg"
                    >
                      <i className="ri-arrow-left-line mr-2"></i>
                      日付選択に戻る
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 説明セクション */}
          {!showResult && (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-8">
              <div className="text-center">
                <i className="ri-calendar-2-line text-6xl text-pink-400 mb-4"></i>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">365日の誕生花</h3>
                <p className="text-gray-600 leading-relaxed">
                  毎日にはそれぞれ特別な誕生花があります。<br />
                  あなたの誕生日や大切な人の誕生日の花を探してみましょう。<br />
                  花言葉とともに、その日の花の魅力をお届けします。
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
